import { ComponentProps, PropsWithChildren, ReactElement, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from './button';
import {
  ModalRoot,
  ModalTriggerButton,
  ModalPortal,
  ModalOverlay,
  ModalContent,
  ModalContentHeader,
  ModalContentBody,
  ModalContentFooter,
  ModalContentActions,
  ModalCancelButton,
  ModalActionButton,
} from '@socialchorus/shared-ui-components';
import useEscapeToClose from '../../common/use-escape-to-close';

type ActionButtonProps = ComponentProps<typeof Button> & {
  text: string;
  onClick: () => void;
};

type DialogProps = PropsWithChildren<{
  label?: string;
  title?: string;
  accept?: ActionButtonProps;
  cancel?: ActionButtonProps;
  body?: React.ReactNode;
  triggerButton?: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'x-large';
  transparent?: boolean;
}>;

const SolarDialog = ({
  label,
  title,
  accept,
  cancel,
  body,
  triggerButton,
  size,
}: DialogProps) => {
  const modalEl = useRef<HTMLDivElement>(null);
  useEscapeToClose(modalEl, () => cancel?.onClick());

  return (
    <ModalRoot>
      {triggerButton ? (
        triggerButton
      ) : (
        <ModalTriggerButton variant="link" label={label || ''} />
      )}
      <ModalPortal>
        <ModalOverlay />
        <ModalContent size={size || 'medium'}>
          <ModalContentHeader title={title || ''} />
          <ModalContentBody>{body}</ModalContentBody>
          <ModalContentFooter>
            <ModalContentActions>
              {cancel && typeof cancel === 'object' ? (
                <ModalCancelButton label={cancel.text} />
              ) : null}

              {accept && typeof accept === 'object' ? (
                <ModalActionButton
                  label={accept.text}
                  onClick={() => accept.onClick()}
                />
              ) : null}
            </ModalContentActions>
          </ModalContentFooter>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};

SolarDialog.propTypes = {
  /** Title displays in the dialog header */
  title: PropTypes.string,
  /** Object that contains instructions for building a `confirm` button action.
      Props provided will be passed to a `<Button>` component. This is
      typically passed an `onClick` attribute for a callback. */
  accept: PropTypes.object,
  /** Object that contains instructions for building a `cancel` button action.
      Props provided will be passed to a `<Button>` component. This is
      typically passed an `onClick` attribute for a callback. */
  cancel: PropTypes.object,
  /** Text or nodes or all kinds of goodies to appear in the dialog body */
  body: PropTypes.node,
  triggerButton: PropTypes.node,

  header: PropTypes.string,
  transparent: PropTypes.bool,
};

export const ID = 'Overlays.SolarDIALOG';

/** @component */
export default SolarDialog;
